import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertTriangle,
  Phone,
  Video,
  Mail,
  Home,
  MessageSquare,
  CheckCircle2,
  Clock,
  AlertCircle,
  User,
  Calendar,
  Search,
  Filter,
  Plus,
  X,
  Edit3,
  Loader2,
} from 'lucide-react'
import { attendanceService, type WelfareCheck, type AbsenceRecord } from '../../services/attendanceService'

// Temporary auth hook
const useAuth = () => {
  const [user] = useState({
    churchId: localStorage.getItem('churchId') || '',
    id: localStorage.getItem('userId') || ''
  })
  return { user }
}

const WelfareChecks: React.FC = () => {
  console.log('[WelfareChecks] Component rendering')

  const { user } = useAuth()
  const [welfareChecks, setWelfareChecks] = useState<WelfareCheck[]>([])
  const [absences, setAbsences] = useState<AbsenceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<WelfareCheck['status'] | 'all'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedCheck, setSelectedCheck] = useState<WelfareCheck | null>(null)

  const loadData = useCallback(async () => {
    console.log('[WelfareChecks] loadData - starting, filterStatus:', filterStatus)
    if (!user?.churchId) {
      console.log('[WelfareChecks] loadData - no churchId')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      console.log('[WelfareChecks] loadData - fetching data')
      const [checksData, absencesData] = await Promise.all([
        attendanceService.getWelfareChecks(
          user.churchId,
          filterStatus === 'all' ? undefined : filterStatus
        ),
        attendanceService.getAbsences(user.churchId, {
          needsWelfareCheck: true
        })
      ])
      console.log('[WelfareChecks] loadData - data fetched:', checksData.length, absencesData.length)
      setWelfareChecks(checksData)
      setAbsences(absencesData)
    } catch (error) {
      console.error('[WelfareChecks] Error loading welfare data:', error)
    } finally {
      console.log('[WelfareChecks] loadData - completed')
      setLoading(false)
    }
  }, [user?.churchId, filterStatus])

  useEffect(() => {
    console.log('[WelfareChecks] useEffect - loadData called')
    loadData()
  }, [loadData])

  const handleTriggerAutoChecks = async () => {
    if (!confirm('This will create welfare checks for all members with 3+ consecutive absences. Continue?')) {
      return
    }

    try {
      const count = await attendanceService.triggerAutoWelfareChecks()
      alert(`Created ${count} welfare checks`)
      await loadData()
    } catch (error) {
      console.error('Error triggering welfare checks:', error)
      alert('Failed to trigger welfare checks')
    }
  }

  const handleUpdateStatus = async (
    checkId: string,
    status: WelfareCheck['status'],
    updates?: Partial<WelfareCheck>
  ) => {
    try {
      await attendanceService.updateWelfareCheck(checkId, {
        status,
        ...updates
      })
      await loadData()
    } catch (error) {
      console.error('Error updating welfare check:', error)
      alert('Failed to update welfare check')
    }
  }

  const handleCompleteCheck = async (
    checkId: string,
    outcome: string,
    outcomeDetails: string,
    followUpNeeded: boolean,
    followUpDate?: string
  ) => {
    try {
      await attendanceService.completeWelfareCheck(
        checkId,
        outcome,
        outcomeDetails,
        followUpNeeded,
        followUpDate
      )
      await loadData()
      setShowDetailModal(false)
    } catch (error) {
      console.error('Error completing welfare check:', error)
      alert('Failed to complete welfare check')
    }
  }

  const filteredChecks = welfareChecks.filter(check =>
    check.member?.preferred_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      urgent: { variant: 'destructive' },
      high: { variant: 'destructive' },
      medium: { variant: 'default' },
      low: { variant: 'secondary' },
    }
    const config = variants[priority] || { variant: 'outline' as const }
    return <Badge variant={config.variant}>{priority.toUpperCase()}</Badge>
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'outline' }> = {
      pending: { variant: 'outline' },
      scheduled: { variant: 'default' },
      in_progress: { variant: 'default' },
      completed: { variant: 'secondary' },
      cancelled: { variant: 'outline' },
    }
    const config = variants[status] || { variant: 'outline' as const }
    return <Badge variant={config.variant}>{status.replace('_', ' ').toUpperCase()}</Badge>
  }

  const getContactMethodIcon = (method?: string) => {
    switch (method) {
      case 'phone':
        return <Phone className="w-4 h-4" />
      case 'visit':
        return <Home className="w-4 h-4" />
      case 'text':
        return <MessageSquare className="w-4 h-4" />
      case 'email':
        return <Mail className="w-4 h-4" />
      case 'video_call':
        return <Video className="w-4 h-4" />
      default:
        return <AlertCircle className="w-4 h-4" />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Welfare Checks</h1>
          <p className="text-muted-foreground">Monitor and manage member wellness follow-ups</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleTriggerAutoChecks} variant="outline" size="sm">
            <AlertTriangle className="mr-2 h-4 w-4" />
            Trigger Auto-Checks
          </Button>
          <Button onClick={() => setShowCreateModal(true)} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Create Check
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Checks</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{welfareChecks.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {welfareChecks.filter(c => c.status === 'pending').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-sky-500">
              {welfareChecks.filter(c => c.status === 'in_progress').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Priority</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {welfareChecks.filter(c => c.priority === 'urgent' || c.priority === 'high').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Need Checks</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {absences.filter(a => a.welfare_check_needed && !a.welfare_check_triggered).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Members Needing Welfare Checks */}
      {absences.filter(a => a.welfare_check_needed && !a.welfare_check_triggered).length > 0 && (
        <Card className="border-orange-600/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <CardTitle>Members Needing Welfare Checks</CardTitle>
            </div>
            <CardDescription>Members with consecutive absences requiring follow-up</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {absences.filter(a => a.welfare_check_needed && !a.welfare_check_triggered).map(absence => (
                <Card key={absence.id} className="bg-muted/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{absence.member?.preferred_name}</p>
                      <Badge variant="destructive" className="text-xs">
                        {absence.consecutive_absences} absences
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">
                      Last service: {formatDate(absence.absence_date)}
                    </p>
                    <Button
                      onClick={() => {
                        setSelectedCheck({
                          id: '',
                          member_id: absence.member_id,
                          church_id: absence.church_id,
                          triggered_by: 'consecutive_absences',
                          priority: 'medium',
                          status: 'pending',
                          follow_up_needed: false,
                          created_at: new Date().toISOString(),
                          updated_at: new Date().toISOString(),
                          member: absence.member
                        } as WelfareCheck)
                        setShowCreateModal(true)
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      Create Welfare Check
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-3">
              <Select value={filterStatus} onValueChange={(value) => setFilterStatus(value as any)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                More Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Welfare Checks List */}
      <div className="flex flex-col gap-3">
        {filteredChecks.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'No welfare checks found matching your search.' : 'No welfare checks found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredChecks.map((check) => (
            <Card key={check.id} className="hover:border-primary transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    {check.member?.photo_url ? (
                      <img
                        src={check.member.photo_url}
                        alt={check.member.preferred_name}
                        className="w-12 h-12 rounded-full"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-6 w-6 text-primary" />
                      </div>
                    )}

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold">
                          {check.member?.preferred_name}
                        </h3>
                        {getPriorityBadge(check.priority)}
                        {getStatusBadge(check.status)}
                      </div>

                      <p className="text-sm text-muted-foreground mb-3">
                        {check.member?.phone_number}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="h-4 w-4" />
                          <span>{check.member?.consecutive_absences || 0} consecutive absences</span>
                        </div>

                        {check.contact_method && (
                          <div className="flex items-center gap-1">
                            {getContactMethodIcon(check.contact_method)}
                            <span className="capitalize">{check.contact_method.replace('_', ' ')}</span>
                          </div>
                        )}

                        {check.scheduled_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            <span>Scheduled: {formatDate(check.scheduled_date)}</span>
                          </div>
                        )}

                        {check.assigned_member && (
                          <div className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            <span>Assigned to: {check.assigned_member.preferred_name}</span>
                          </div>
                        )}
                      </div>

                      {check.contact_notes && (
                        <p className="mt-3 text-sm text-muted-foreground italic">
                          "{check.contact_notes}"
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {check.status === 'pending' && (
                      <Button
                        onClick={() => handleUpdateStatus(check.id, 'in_progress')}
                        variant="outline"
                        size="sm"
                      >
                        Start
                      </Button>
                    )}

                    {(check.status === 'in_progress' || check.status === 'scheduled') && (
                      <Button
                        onClick={() => {
                          setSelectedCheck(check)
                          setShowDetailModal(true)
                        }}
                        size="sm"
                      >
                        Complete
                      </Button>
                    )}

                    <Button
                      onClick={() => {
                        setSelectedCheck(check)
                        setShowDetailModal(true)
                      }}
                      variant="ghost"
                      size="sm"
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Complete Welfare Check Modal */}
      {showDetailModal && selectedCheck && (
        <CompleteCheckModal
          check={selectedCheck}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedCheck(null)
          }}
          onComplete={handleCompleteCheck}
        />
      )}
    </div>
  )
}

// Complete Check Modal Component
interface CompleteCheckModalProps {
  check: WelfareCheck
  onClose: () => void
  onComplete: (
    checkId: string,
    outcome: string,
    outcomeDetails: string,
    followUpNeeded: boolean,
    followUpDate?: string
  ) => Promise<void>
}

const CompleteCheckModal: React.FC<CompleteCheckModalProps> = ({ check, onClose, onComplete }) => {
  const [outcome, setOutcome] = useState('')
  const [outcomeDetails, setOutcomeDetails] = useState('')
  const [followUpNeeded, setFollowUpNeeded] = useState(false)
  const [followUpDate, setFollowUpDate] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onComplete(check.id, outcome, outcomeDetails, followUpNeeded, followUpDate || undefined)
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Complete Welfare Check</DialogTitle>
          <DialogDescription>
            Record the outcome of this welfare check and any follow-up actions needed
          </DialogDescription>
        </DialogHeader>

        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <p className="text-lg font-semibold mb-1">{check.member?.preferred_name}</p>
            <p className="text-sm text-muted-foreground">
              {check.member?.consecutive_absences} consecutive absences
            </p>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="outcome">Outcome *</Label>
            <Select required value={outcome} onValueChange={setOutcome}>
              <SelectTrigger id="outcome">
                <SelectValue placeholder="Select outcome..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contacted_well">Contacted - Member is well</SelectItem>
                <SelectItem value="contacted_needs_support">Contacted - Needs support</SelectItem>
                <SelectItem value="contacted_no_concerns">Contacted - No concerns</SelectItem>
                <SelectItem value="unable_to_reach">Unable to reach</SelectItem>
                <SelectItem value="declined_contact">Declined contact</SelectItem>
                <SelectItem value="referred_pastoral_care">Referred to pastoral care</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="details">Details *</Label>
            <Textarea
              id="details"
              required
              value={outcomeDetails}
              onChange={(e) => setOutcomeDetails(e.target.value)}
              rows={4}
              placeholder="Provide details about the contact and outcome..."
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="followUpNeeded"
              checked={followUpNeeded}
              onCheckedChange={(checked) => setFollowUpNeeded(checked as boolean)}
            />
            <Label htmlFor="followUpNeeded" className="text-sm font-normal cursor-pointer">
              Follow-up needed
            </Label>
          </div>

          {followUpNeeded && (
            <div className="space-y-2">
              <Label htmlFor="followUpDate">Follow-up Date</Label>
              <Input
                id="followUpDate"
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Complete Check</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default WelfareChecks
